import { useState, useCallback } from 'react';

import EditIcon from '@mui/icons-material/EditOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import _, { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import { withRouter } from 'providers';
import { toast } from 'react-toastify';

import acl from 'constants/acl';
import { STATUS_COLOR_CODE } from 'themes/colors';
import AxlButton from '../../../../AxlMUIComponent/AxlButton';
import AssignDriverModal from '../../../../components/AssignDriverModal';
import EditPickupWindowModal from '../../../../components/EditPickupWindowModal';
import AssignmentNote from '../../../../components/AssignmentNote';
import DriverInfoCard from '../../../../components/DriverInfoCard';
import ReorderShipment from '../../../../components/ReorderShipment';
import TimeWindow from '../../../../components/TimeWindow';
import { convertMeterToMile } from '../../../../constants/common';
import { getStyles } from './styles';
import TimeWithTimezone from 'components/TimeWithTimezone';
import TrackingBadge from 'components/TrackingBadge';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDurationHourMinute } from 'Utils/date';

const EST_ROUTE_TIME_TOOLTIP = 'Est. route time is the total of the values below:';
const ETC_TOOLTIP =
  "Estimated time of completion based on driver's current time, location and remaining stops on the route - excluding returns.";
const DRIVER_OTD_TOOLTIP =
  "The route's must-deliver-by time: driver needs to complete the route by this time to be considered on-time.";


function AssignmentDetail(props) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { store, history, onShowMessenger } = props;

  const [showToggleActivateAssignmentModal, setShowToggleActivateAssignmentModal] = useState(false);
  const [showAssignDriver, setShowAssignDriver] = useState(false);
  const [showEditPickupWindow, setShowEditPickupWindow] = useState(false);

  const { assignmentStore, userStore, permissionStore } = store;
  const { selectedAssignment: assignmentInfo } = assignmentStore;

  const refreshAssignment = useCallback(() => {
    assignmentStore.loadAssignments();
    assignmentStore.loadAssignment(assignmentStore.selectedAssignmentId);
  }, [assignmentStore]);

  const onHideAssignDriver = useCallback(() => {
    setShowAssignDriver(false);
  }, []);

  const onUnAssignDriver = useCallback((reason) => {
    assignmentStore.unassign(assignmentInfo, reason)
      .then(() => {
        refreshAssignment();
      })
      .catch((err) => {
        toast.error(_.get(err, 'data.message', 'Failed to unassign driver. Please try again.'));
      });
  }, [assignmentStore, assignmentInfo, refreshAssignment]);

  const onShowAssignDriverHandler = useCallback(() => {
    setShowAssignDriver(true);
  }, []);

  const onShowEditPickupWindow = useCallback(() => {
    setShowEditPickupWindow(true);
  }, []);

  const onHideEditPickupWindow = useCallback(() => {
    setShowEditPickupWindow(false);
  }, []);

  const onCloseToggleActivateAssignmentModal = useCallback(() => {
    setShowToggleActivateAssignmentModal(false);
  }, []);

  const onConfirmToggleActivateAssignment = useCallback((isActivated) => {
    if (!isActivated) {
      assignmentStore.activate(assignmentInfo.assignment, (r) => {
        if (!r.ok) {
          if (!r.data.message) {
            toast.error('Cannot active driver!');
          } else {
            toast.error(r.data.message);
          }
        } else {
          onCloseToggleActivateAssignmentModal();
          assignmentStore.loadAssignments();
        }
      });
    } else {
      assignmentStore.deactivate(assignmentInfo.assignment).then(() => {
        onCloseToggleActivateAssignmentModal();
        assignmentStore.loadAssignments();
      });
    }
  }, [assignmentStore, assignmentInfo, onCloseToggleActivateAssignmentModal]);

  if (!assignmentInfo?.assignment) return null;

  const { user } = userStore;
  const {
    assignment,
    driver: assignmentDriver,
    isCompleted,
    isPickedUp,
    delivery_by_date_time,
    deliver_by_at_risk_threshold,
    estimated_completion_ts,
  } = assignmentInfo;

  const { travel_time = 0, service_time = 0, pickup_time = 0 } = assignment;
  const estDeliveryTime = Math.round((travel_time + service_time) / 60) * 60; // ensure total always = estDeliveryTime + estPickupTime after formatting to hour/minute
  const estPickupTime = Math.round(pickup_time / 60) * 60;
  const totalEstRouteTime = estDeliveryTime + estPickupTime;

  const isActivated = assignment?.driver_id && assignment.is_active;
  const isShowDriverAction = assignment?.driver_id && assignment.status !== 'COMPLETED';

  const statusColor = assignment.status ? STATUS_COLOR_CODE[assignment.status] : STATUS_COLOR_CODE['PENDING'];
  const isAvailableMessageButton = false; // Disabled in this release
  const isDeniedActive = permissionStore.isDenied(acl.ASSIGNMENTS.ACTIVE);
  const isDeniedReorder = permissionStore.isDenied(acl.ASSIGNMENTS.ORDER_SHIPMENTS);
  const isDeniedMessenger = permissionStore.isDenied(acl.ASSIGNMENTS.MESSENGER);
  const isDeniedRebookEta = permissionStore.isDenied(acl.ASSIGNMENTS.REBOOK_ETA);

  return (
    <Box p={1} sx={styles.container}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid>
          <Box sx={styles.title}>
            {`Assignment ${_.get(assignment, 'label', 'N/A')} Details`}
            {assignment && <Box component="code" sx={styles.highlightCode}>{assignment.id}</Box>}
          </Box>
        </Grid>
        <Grid>
          <Box sx={styles.statusBox}>
            Status:{' '}
            <Box component="span" sx={{ ...styles.status, color: statusColor }}>
              {assignment.status || '-'}
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container alignItems="flex-start" justifyContent={"space-between"} style={{ marginTop: 16 }}>
        {/* Before pickup */}
        {!isPickedUp && (
          <>
            <TimeWindow
              variant="tooltip"
              label="PICKUP WINDOW"
              startTs={assignment.start_pickup_time}
              endTs={assignment.end_pickup_time}
              timezone={assignment.timezone}
              labelSx={styles.labelRowSpaced}
              labelAdornment={
                isShowDriverAction && !isDeniedRebookEta ? (
                  <IconButton
                    size="small"
                    aria-label="edit pickup window"
                    onClick={onShowEditPickupWindow}
                    sx={{ p: 0 }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                ) : null
              }
            />

            <Box>
              <Box sx={[styles.label, styles.labelRowSpaced]}>
                <Tooltip
                  placement="top"
                  arrow
                  title={
                    <>
                      <div>{EST_ROUTE_TIME_TOOLTIP}</div>
                      <div>{`+ Est. Delivery Time: ${formatDurationHourMinute(estDeliveryTime)}`}</div>
                      <div>{`+ Est. Pickup Time: ${formatDurationHourMinute(estPickupTime)}`}</div>
                    </>
                  }
                >
                  <Box component="span" display="inline-flex" alignItems="center" style={{ gap: 2 }}>
                    {`EST. ROUTE TIME`}
                    <InfoOutlinedIcon style={{ fontSize: 12 }} />
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={styles.text}>{formatDurationHourMinute(totalEstRouteTime)}</Box>
            </Box>
          </>
        )}

        {/* After pickup  */}
        {(isPickedUp && !isCompleted)&& (
            <TimeWithTimezone
              variant="tooltip"
              label={
                <>
                  <Tooltip title={ETC_TOOLTIP} placement="top" arrow>
                    <Box component="span" display="inline-flex" alignItems="center" style={{ gap: 2 }}>
                      {`ETC`}
                      <InfoOutlinedIcon style={{ fontSize: 12 }} />
                    </Box>
                  </Tooltip>
                  <TrackingBadge
                    targetTime={delivery_by_date_time ? moment(delivery_by_date_time).valueOf() : null}
                    actualTime={estimated_completion_ts}
                    threshold={deliver_by_at_risk_threshold * 60} // minutes → seconds
                  />
                </>
              }
              labelSx={styles.labelRow}
              timeTs={estimated_completion_ts}
              timezone={assignment.timezone}
            />
        )}

        {isCompleted && (
            <TimeWithTimezone
              variant="tooltip"
              label="COMPLETION TIME"
              labelSx={styles.labelRow}
              timeTs={assignment.actual_end_ts}
              timezone={assignment.timezone}
            />
        )}

        {isPickedUp && (
            <TimeWithTimezone
              variant="tooltip"
              label={
                <Tooltip title={DRIVER_OTD_TOOLTIP} placement="top" arrow>
                  <Box component="span" display="inline-flex" alignItems="center" style={{ gap: 2 }}>
                    {`DRIVER OTD`}
                    <InfoOutlinedIcon style={{ fontSize: 12 }} />
                  </Box>
                </Tooltip>
              }
              labelSx={styles.labelRow}
              timeTs={delivery_by_date_time}
              timezone={assignment.timezone}
            />
        )}

        
        <Box>
          <Box sx={[styles.label, styles.labelRowSpaced]}>EST. DISTANCE</Box>
          <Box sx={styles.text}>{`${convertMeterToMile(assignment.travel_distance)} mi`}</Box>
        </Box>
        
      </Grid>
      <Box sx={styles.driverSection}>
        <Box sx={styles.driverScrollArea}>
          <Box mt={1}>
            <AssignmentNote assignmentId={assignmentInfo.assignment.id} userId={user.id} />
            <DriverInfoCard
              history={history}
              assignment={assignmentInfo}
              driver={assignmentDriver}
              driverScore={assignmentInfo.driverScore}
              isActivated={isActivated}
              isCompleted={isCompleted}
              onUnAssignDriver={onUnAssignDriver}
              showDriverAssign={onShowAssignDriverHandler}
            />
          </Box>
        </Box>
        <Box width={1} pt={1} flexShrink={0}>
          <Box sx={styles.footerScroll}>
            {!isDeniedActive && isShowDriverAction && (
              <Button
                variant="contained"
                color="primary"
                disableElevation
                size='small'
                disabled={(!isActivated && !assignment.driver_id) || isCompleted}
                onClick={() => setShowToggleActivateAssignmentModal(true)}
                startIcon={!isActivated && <RocketLaunchIcon />}
              >
                {isActivated ? 'De-Activate' : 'Activate'}
              </Button>
            )}
            <Grid flex={1} />
            {!isDeniedReorder && !isCompleted && <ReorderShipment assignmentStore={assignmentStore} />}
            {!isDeniedMessenger && isAvailableMessageButton && (
              <AxlButton
                variant="outlined"
                bgcolor="primary.whiteTwo"
                color="primary.graySecond"
                icon
                ico={{ className: 'fa fa-comment-o' }}
                disabled={(!isActivated && !assignment.driver_id) || isCompleted}
                onClick={onShowMessenger}
              >
                <i className={`fa fa-comment-o`} />
                &nbsp;{`Driver`}
              </AxlButton>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        maxWidth="lg"
        open={showToggleActivateAssignmentModal}
        onClose={onCloseToggleActivateAssignmentModal}
        transitionDuration={{ exit: 0 }}
        PaperProps={{ sx: styles.dialogPaper }}
      >
        <DialogTitle sx={styles.dialogTitle}>{isActivated ? 'Deactivate Assignment' : 'Activate Assignment'}</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          {`Please confirm that you want to ${isActivated ? 'deactivate' : 'activate'} assignment?`}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-evenly' }}>
          <Button variant="outlined" disableElevation sx={styles.dialogButton} onClick={onCloseToggleActivateAssignmentModal}>
            NO
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            sx={styles.dialogButton}
            onClick={() => onConfirmToggleActivateAssignment(isActivated)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <AssignDriverModal
        open={showAssignDriver}
        assignmentInfo={assignmentInfo}
        onClose={onHideAssignDriver}
        onCompleted={refreshAssignment}
      />

      <EditPickupWindowModal
        open={showEditPickupWindow}
        assignmentInfo={assignmentInfo}
        onClose={onHideEditPickupWindow}
        onCompleted={refreshAssignment}
      />
    </Box>
  );
}

export default compose(withRouter, inject('store'), observer)(AssignmentDetail);
